// The timeouts that eventually drop the tenant key, split out of
// `vault-unlock.ts` so that file can be about key material and this one
// about wall-clock time. ADR 0009 ("Session shape") asks for three things
// and this module is all three of them, plus the absolute cap the
// security review added (`MAX_SESSION_MS`, below):
//
//   1. 8 hours of inactivity, sliding — every signal of life pushes it out.
//   2. ONE rearmed `setTimeout`, never a poll. A `setInterval` checking
//      "has it been 8 hours yet?" wakes the tab ~28,800 times to answer
//      "no" 28,799 of them, and on a laptop that is a battery cost paid for
//      nothing. A timer that is armed once and re-armed on activity costs
//      exactly as many wakeups as there are phases.
//   3. A warning 60s before the lock, so the lock is never a surprise
//      mid-task. The warning is exposed as subscribable state rather than
//      rendered here: this module must stay importable on the server (see
//      `vault-unlock.ts`'s SSR note), and it has no business knowing what a
//      component looks like.
//
// The countdown therefore runs in two phases off a single live timer:
// arm for (TIMEOUT - LEAD) → raise the warning → arm for LEAD → expire.
// Activity during either phase restarts from phase one and clears the
// warning — but never past the absolute deadline (`MAX_SESSION_MS`),
// which activity cannot move at all.
//
// A further thing the ADR asks for by implication and a bare `setTimeout`
// does not give: the deadline has to be WALL CLOCK, not elapsed timer
// time. Timers do not advance while a machine is suspended, so "a laptop
// left unlocked overnight is not still holding the tenant key in the
// morning" — the ADR's own words for what this exists to prevent — is
// precisely the case a `setTimeout` gets wrong: close the lid at 17:05,
// open it at 09:00, and the 8-hour timer has eight hours still to run. So
// the deadline is stored as a `Date.now()` instant, every timer wakeup
// re-reads the clock rather than trusting that it slept the right amount,
// and the tab re-checks when it comes back to the foreground, which on a
// resumed machine is the first moment anything in this module runs again.

// The two wake signals are `visibilitychange` on the document (lid-open,
// tab-switch) and `focus` on the window (a window raised without a
// visibility transition). Neither carries any meaning here beyond
// "re-read the clock now", and both are attached only while a countdown
// is actually running.

/** 8 hours. Long enough that an operator working a normal day is not
 * interrupted; short enough that a laptop left unlocked overnight is not
 * still holding the tenant key in the morning. ADR 0009 is explicit that
 * this number is a mitigation for the threat model getting *worse* (the
 * key is now one touch away rather than requiring the recovery file), not
 * a nicety. */
export const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000

/**
 * 24 hours from the unlock, and NOTHING extends it.
 *
 * A sliding timeout alone has no upper bound: as long as something keeps
 * reporting activity, the key stays in the page forever. That "something"
 * does not have to be an operator. An XSS that calls one vault-reading
 * code path a few times an hour renews the 8-hour window indefinitely and
 * the session never ends on its own — which turns a browser-lifetime
 * compromise into an unbounded one, the exact outcome ADR 0009's timeout
 * exists to prevent.
 *
 * So the deadline is two numbers, and the earlier wins. The sliding one
 * answers "has anyone used this recently?"; this one answers "how long
 * may a single unlock last, no matter what?".
 *
 * Why 24h and not something tighter: the cap is not the primary control —
 * the 8-hour idle timeout is, and it is what ends a normal session. This
 * is the backstop for the case where the idle timer is being renewed by
 * something that is not a person, and the only property that matters
 * there is that it is FINITE and short enough that the window closes
 * within a day. A day also has the operational virtue of being
 * predictable: whatever the shift pattern, an unlock does not survive
 * into tomorrow's. Tightening it to, say, 12h would start locking real
 * operators who unlock at the beginning of a long day, which trains them
 * to keep the recovery file handy — and keeping the recovery file handy
 * is the behaviour ADR 0009 exists to stop. One touch a day is a price
 * that gets paid; one every few hours is a price that gets routed
 * around.
 */
export const MAX_SESSION_MS = 24 * 60 * 60 * 1000

/** How long the warning is on screen before the lock actually happens. */
export const IDLE_WARNING_LEAD_MS = 60_000

/** The live timer for whichever phase we are in, or `null` when no
 * countdown is running (i.e. while the vault is locked). */
let timer: ReturnType<typeof setTimeout> | null = null

/** What to do when the countdown runs out. Held rather than passed so
 * `noteActivity()` — which fires from event handlers that know nothing
 * about locking — can rearm without being handed the callback again. */
let onExpire: (() => void) | null = null

/** The sliding deadline: `IDLE_TIMEOUT_MS` from the last signal of life,
 * or `null` when no countdown is running. The `setTimeout` is only a way
 * of being woken near it, and every wakeup re-reads the clock against
 * this rather than assuming it slept for as long as it was asked to. */
let idleDeadlineAt: number | null = null

/** The absolute deadline: `MAX_SESSION_MS` from the unlock, set once in
 * `startIdleCountdown` and never touched again until the next one.
 * `noteActivity` deliberately has no access to it. */
let maxSessionDeadlineAt: number | null = null

/** The instant the key actually goes: whichever deadline comes first.
 * `null` only when no countdown is running, in which case both are.
 *
 * Expressed as a function rather than a third stored number so there is
 * no state to keep in sync — a `noteActivity` that updated a cached
 * effective deadline but forgot to re-clamp it is precisely the bug the
 * cap is here to not have. */
function effectiveDeadline(): number | null {
  if (idleDeadlineAt === null || maxSessionDeadlineAt === null) return null
  return Math.min(idleDeadlineAt, maxSessionDeadlineAt)
}

let isWakeListening = false

let warningActive = false

const warningListeners = new Set<() => void>()

function notifyWarningListeners(): void {
  for (const listener of warningListeners) listener()
}

function setWarning(active: boolean): void {
  if (warningActive === active) return
  warningActive = active
  notifyWarningListeners()
}

/** Subscribes to the T-60s warning appearing and disappearing. Shaped for
 * `useSyncExternalStore`; see `useIdleWarning` in `vault-unlock.ts`. */
export function subscribeIdleWarning(listener: () => void): () => void {
  warningListeners.add(listener)
  return () => {
    warningListeners.delete(listener)
  }
}

/** Whether the "you are about to be locked out" affordance should be on
 * screen right now. */
export function isIdleWarningActive(): boolean {
  return warningActive
}

function clearTimer(): void {
  if (timer === null) return
  clearTimeout(timer)
  timer = null
}

/** The key's time is up. Clears the countdown first, so a callback that
 * does not itself stop the timer (every real one does, via `lock()`)
 * still cannot leave an armed timer behind. */
function expireNow(): void {
  clearTimer()
  idleDeadlineAt = null
  maxSessionDeadlineAt = null
  setWarning(false)
  // Read through the module variable rather than closing over a
  // parameter: a `startIdleCountdown` that replaced the callback
  // mid-countdown must be honoured, not shadowed by a stale closure.
  onExpire?.()
}

/**
 * Puts the state machine where the wall clock says it should be, and arms
 * one timer for the next transition.
 *
 * Called on every arm, on every timer wakeup, and whenever the tab comes
 * back to the foreground — the same function each time, because "what
 * phase are we in" has exactly one correct answer and it is a subtraction
 * against `effectiveDeadline()`, not a count of how many timers have
 * fired. A wakeup that finds the deadline already past expires
 * immediately instead of starting a fresh 60-second warning nobody is
 * there to read.
 *
 * Reading the deadline through `effectiveDeadline()` is what makes the
 * absolute cap real: the warning and the lock both land on whichever of
 * the two deadlines is nearer, so a session renewed right up to the
 * 24-hour mark still gets its 60 seconds of warning and then ends.
 */
function armFromDeadline(): void {
  clearTimer()
  const deadline = effectiveDeadline()
  if (deadline === null) return
  const remaining = deadline - Date.now()
  if (remaining <= 0) {
    expireNow()
    return
  }
  // Phase two: the warning is up and the key goes when it runs out.
  if (remaining <= IDLE_WARNING_LEAD_MS) {
    setWarning(true)
    timer = setTimeout(armFromDeadline, remaining)
    return
  }
  // Phase one: quiet until the warning is due.
  setWarning(false)
  timer = setTimeout(armFromDeadline, remaining - IDLE_WARNING_LEAD_MS)
}

/** Re-reads the clock when the tab comes back. A suspended machine's
 * timers do not advance, so this — not the `setTimeout` — is what makes
 * "left unlocked overnight" lock on resume rather than eight waking hours
 * later. Attached only while a countdown is running, and lazily, so the
 * module stays importable where there is no `document` (SSR on a
 * Worker). */
function attachWakeListeners(): void {
  if (isWakeListening) return
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  document.addEventListener('visibilitychange', armFromDeadline)
  window.addEventListener('focus', armFromDeadline)
  isWakeListening = true
}

function detachWakeListeners(): void {
  if (!isWakeListening) return
  document.removeEventListener('visibilitychange', armFromDeadline)
  window.removeEventListener('focus', armFromDeadline)
  isWakeListening = false
}

/** Starts (or restarts) the countdown. Called on every unlock, so BOTH
 * deadlines are measured from the moment the key arrived in this tab.
 *
 * This is the only place `maxSessionDeadlineAt` is ever set, which is
 * what makes `noteActivity` unable to extend the cap: the arithmetic that
 * slides a deadline lives in this module and simply cannot reach it.
 *
 * WHAT THE CAP DOES NOT BOUND, stated here because an earlier version of
 * this comment claimed it did. It used to say an unlock "is a fresh human
 * gesture". Since ADR 0009 that is false: `tryStoredUnlock` adopts the
 * key from IndexedDB with no gesture at all, and it calls `adopt()`,
 * which calls this. So on a device that was told to REMEMBER the key,
 * anything that can run script on the page can lock and re-unlock at
 * will, and the 24-hour cap bounds nothing against it — the key is on the
 * disk, and re-arming is free.
 *
 * The cap is still worth having, for the two cases it does bound: a
 * memory-only session (nothing stored, so a lock is final until the next
 * paste), and an unattended tab nobody is driving. It is not a defence
 * against an attacker with script on a remembered device; ADR 0009's
 * "an attacker with the unlocked browser profile can use the vault" is
 * the honest statement of that, and this is the same fact seen from the
 * timer's side. */
export function startIdleCountdown(expire: () => void): void {
  onExpire = expire
  attachWakeListeners()
  const now = Date.now()
  idleDeadlineAt = now + IDLE_TIMEOUT_MS
  maxSessionDeadlineAt = now + MAX_SESSION_MS
  armFromDeadline()
}

/** Stops the countdown and clears any warning. Called on lock, from either
 * side: there is nothing left to time out. */
export function stopIdleCountdown(): void {
  clearTimer()
  detachWakeListeners()
  idleDeadlineAt = null
  maxSessionDeadlineAt = null
  onExpire = null
  setWarning(false)
}

/** Slides the IDLE deadline back out to a full `IDLE_TIMEOUT_MS`, and
 * dismisses the warning if it was up — this is both "the operator used
 * the key" and the "stay unlocked" button, because those are the same
 * event as far as the timer is concerned.
 *
 * It does not touch `maxSessionDeadlineAt`, and must not: the cap is the
 * one deadline no amount of activity can push out, so the arithmetic
 * that moves a deadline lives here and the cap is simply not in reach of
 * it. Sliding past the cap is harmless anyway — `armFromDeadline` takes
 * the minimum — but leaving the cap unwritten is what makes that a
 * property of the code rather than of a clamp someone could later
 * forget.
 *
 * A no-op when no countdown is running, so an activity handler left
 * attached after a lock cannot resurrect the timer without a key. */
export function noteActivity(): void {
  if (onExpire === null) return
  idleDeadlineAt = Date.now() + IDLE_TIMEOUT_MS
  armFromDeadline()
}
